import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { userPool as userpool, PRIVACY_POLICY_URL } from '../constants/constants';
import { getGoogleSignInUrl } from '../authConfig';
import { setAuthToken, setRefreshToken, setTokenExpiry, setLoggedInUser } from '../utils/auth';
import { hasCreatedRealTree } from '../utils/onboarding';
import { COLORS } from '../talkTreeShared/Tree/constants/theme';

const cognitoAppClientId = userpool.clientId;
function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const defaultTheme = createTheme({
  typography: {
    // New brand typeface.
    fontFamily: "'Mulish', 'Noto Sans', sans-serif",
  },
});

// The required group for accessing this portal.
const REQUIRED_GROUP = 'talktrees_instructors';

export default function Login() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  // Load the Mulish webfont once (safe no-op if you already link it in index.html).
  useEffect(() => {
    const id = 'mulish-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    /* global mixpanel */
    mixpanel.reset();
    let arrivedViaSignupRedirect = false;
    try {
      sessionStorage.removeItem('tutor_session');
      if (sessionStorage.getItem('signupSuccess') === '1') {
        arrivedViaSignupRedirect = true;
        setShowWelcomeMessage(true);
        sessionStorage.removeItem('signupSuccess');
      }
    } catch {
      // Ignore storage errors in restricted browser contexts.
    }
    mixpanel.track('loginPageViewed', { arrivedViaSignupRedirect });
  }, []);

  const handleLogin = (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    const normalizedUsername = username.toLowerCase().trim();
    const authenticationDetails = new AuthenticationDetails({ Username: normalizedUsername, Password: password });
    const cognitoUser = new CognitoUser({ Username: normalizedUsername, Pool: userpool });

    mixpanel.track('loginAttempted', { method: 'Email', username: normalizedUsername });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        // Get the ID token payload to check group membership
        const idToken = result.getIdToken();
        const payload = idToken.decodePayload();
        const userGroups = payload['cognito:groups'] || [];
        // Validate that the user belongs to the required group
        if (!userGroups.includes(REQUIRED_GROUP)) {
          setLoading(false);
          mixpanel.track('loginFailed', { method: 'Email', username: normalizedUsername, error: 'User not in required group' });
          setError('You do not have access to the Talk Trees portal. Please sign up with an instructor registration code.');
          cognitoUser.signOut();
          return;
        }

        setAuthToken(idToken.getJwtToken());
        setRefreshToken(result.getRefreshToken().getToken());
        setTokenExpiry(idToken.getExpiration());
        setLoggedInUser(normalizedUsername);

        mixpanel.identify(normalizedUsername);
        mixpanel.people.set({ lastLoginMethod: 'Email', role: 'tutor' });
        mixpanel.register({ loginMethod: 'Email' });
        const hasOnboarded = hasCreatedRealTree(normalizedUsername) || payload['custom:onboarded'] === '1';
        mixpanel.track('userLoggedIn', {
          method: 'Email',
          username: normalizedUsername,
          routedTo: hasOnboarded ? 'Dashboard' : 'WelcomeOnboarding',
        });

        localStorage.removeItem('tt_welcome_dismissed');
        try { sessionStorage.removeItem('tutor_session'); } catch { /* ignore */ }

        setLoading(false);
        navigate(hasOnboarded ? '/Dashboard' : '/WelcomeOnboarding', { replace: true });
      },
      onFailure: (err) => {
        setLoading(false);
        mixpanel.track('loginFailed', { method: 'Email', username: normalizedUsername, error: err.code || err.message || 'unknown' });
        if (err.code === 'NotAuthorizedException') {
          setError('Incorrect username or password.');
        } else if (err.code === 'UserNotFoundException') {
          setError('No account found with this username.');
        } else {
          setError(err.message || 'Login failed. Please try again.');
        }
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        setLoading(false);
        console.log('New password required:', userAttributes, requiredAttributes);
        setError('Password reset required. Please contact support.');
      }
    });
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
    },
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: COLORS.page }}>
        {/* Left illustration panel — lighter, airier background */}
        <Box
          sx={{
            width: { xs: 0, md: '46%' },
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            background: COLORS.panelGradient,
            position: 'sticky',
            top: 0,
            height: '100vh',
          }}
          aria-hidden="true"
        >
          <Box
            component="img"
            src="/SampleTree.png"
            alt=""
            role="presentation"
            sx={{ width: '62%', maxWidth: 420, objectFit: 'contain' }}
          />
        </Box>

        {/* Right form panel — no card, generous whitespace */}
        <Box
          component="main"
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}
        >
          <Container maxWidth="xs" disableGutters>
            <Box sx={{ width: '100%' }}>
              {/* Heading with inline TalkTrees mark */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  mb: 5,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: 500,
                    fontSize: { xs: 26, sm: 32 },
                    letterSpacing: '0px',
                    color: COLORS.primary,
                    lineHeight: 1.1,
                  }}
                >
                  Log in to
                </Typography>
                <Box
                  component="img"
                  src="/TTLogo.svg"
                  alt="TalkTrees"
                  sx={{ height: { xs: 30, sm: 38 }, width: 'auto', display: 'block', flexShrink: 0 }}
                />
              </Box>

              <Box aria-live="polite" aria-atomic="true">
                {showWelcomeMessage && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
                    Thank you for signing up. You can now log in.
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
                    {error}
                  </Alert>
                )}
              </Box>

              <Box
                component="form"
                onSubmit={handleLogin}
                noValidate
                aria-busy={loading}
                aria-label="Login form"
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Email"
                  name="username"
                  autoComplete="email"
                  autoFocus
                  inputProps={{ 'aria-required': true }}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  sx={fieldSx}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  inputProps={{ 'aria-required': true }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={loading}
                  sx={{
                    mt: 3.5,
                    mb: 3,
                    px: 5,
                    backgroundColor: COLORS.primary,
                    borderRadius: '4px',
                    py: 1.1,
                    fontWeight: 700,
                    fontSize: 15,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: COLORS.primaryHover },
                  }}
                >
                  {loading ? 'Logging In...' : 'Log in'}
                </Button>

                {/* ---- Optional: Google sign-in (logic preserved from original) ---------
                    The new mockup doesn't show this block. It's kept so the Google auth
                    method still works. Delete from here to the matching comment below if
                    you want the minimal, mockup-exact look. */}
                <Divider sx={{ my: 2, color: COLORS.textMuted }} role="separator">or</Divider>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  startIcon={<GoogleIcon />}
                  sx={{
                    mb: 3,
                    borderColor: COLORS.border,
                    color: COLORS.text,
                    borderRadius: '4px',
                    py: 1.2,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { borderColor: COLORS.primary, backgroundColor: COLORS.tintedHover },
                  }}
                  onClick={() => {
                    const redirectUri = `${window.location.origin}/auth/callback`;
                    window.location.href = getGoogleSignInUrl(cognitoAppClientId, redirectUri);
                  }}
                >
                  Log in with Google
                </Button>
                {/* ---- end optional Google sign-in block ------------------------------ */}

                <Grid container direction="column" alignItems="center" spacing={1.5}>
                  <Grid item>
                    <span style={{ color: COLORS.textMuted }}>Don&apos;t have an account?</span>{' '}
                    <Link component={RouterLink} to="/signup" variant="body2" style={{ color: COLORS.primary, textDecoration: 'underline', fontWeight: 600 }}>
                      Sign up
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link component={RouterLink} to="/forgot-password" variant="body2" style={{ color: COLORS.primary, textDecoration: 'underline', fontWeight: 600 }}>
                      Forgot password?
                    </Link>
                  </Grid>
                </Grid>
              </Box>

              <Box
                component="footer"
                sx={{ mt: 6, textAlign: 'center', color: COLORS.textMuted, fontSize: 13, lineHeight: 1.6 }}
              >
                By logging in, you accept our{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="talk-trees-privacy"
                  rel="noreferrer"
                  style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>{' '}
                and how we keep your info safe.
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}